import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Loader2, Users, X, Info } from 'lucide-react';

const initialFormData = {
  firstName: '',
  middleName: '',
  lastName: '',
  preferredName: '',
  dateOfBirth: '',
  generation: '2',
  familyRole: '',
  corporateRoles: [],
  directorId: '',
  email: '',
  phone: '',
  address: '',
  driversLicence: '',
  confidential: false
};

const FAMILY_ROLES = [
  { value: 'Patriarch', label: 'Patriarch', description: 'Male family head (Gen 1)' },
  { value: 'Matriarch', label: 'Matriarch', description: 'Female family head (Gen 1)' },
  { value: 'Spouse', label: 'Spouse', description: 'Married partner' },
  { value: 'Partner', label: 'Partner / De Facto', description: 'Unmarried partner' },
  { value: 'Son', label: 'Son', description: 'Male child' },
  { value: 'Daughter', label: 'Daughter', description: 'Female child' },
  { value: 'Son-in-law', label: 'Son-in-law', description: 'Spouse of daughter' },
  { value: 'Daughter-in-law', label: 'Daughter-in-law', description: 'Spouse of son' },
  { value: 'Grandson', label: 'Grandson', description: 'Male grandchild' },
  { value: 'Granddaughter', label: 'Granddaughter', description: 'Female grandchild' },
  { value: 'Sibling', label: 'Sibling', description: 'Brother or sister' },
  { value: 'Extended Family', label: 'Extended Family', description: 'Other family members' }
];

const CORPORATE_ROLES = [
  { value: 'Director', label: 'Director' },
  { value: 'Trustee', label: 'Trustee' },
  { value: 'Beneficiary', label: 'Beneficiary' },
  { value: 'Shareholder', label: 'Shareholder' },
  { value: 'Appointor', label: 'Appointor' },
  { value: 'Guardian', label: 'Guardian' },
  { value: 'Secretary', label: 'Company Secretary' },
  { value: 'Executor', label: 'Executor' },
  { value: 'Power of Attorney', label: 'Power of Attorney' }
];

export default function AddIndividualModal({ isOpen, onClose, onSave, clientId, initialData, isEdit }) {
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEdit && initialData) {
      setFormData({
        firstName: initialData.firstName || '',
        middleName: initialData.middleName || '',
        lastName: initialData.lastName || '',
        preferredName: initialData.preferredName || '',
        dateOfBirth: initialData.dateOfBirth || '',
        generation: String(initialData.generation || '2'),
        familyRole: initialData.familyRole || initialData.role || '', // Support legacy 'role' field
        corporateRoles: initialData.corporateRoles || [],
        directorId: initialData.directorId || '',
        email: initialData.email || '',
        phone: initialData.phone || '',
        address: initialData.address || '',
        driversLicence: initialData.driversLicence || '',
        confidential: initialData.confidential || false
      });
    } else {
      setFormData(initialFormData);
    }
  }, [isEdit, initialData, isOpen]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    if (!formData.familyRole) newErrors.familyRole = 'Family role is required';
    
    // Validate Director ID format if provided (15 digits)
    if (formData.directorId && !/^\d{15}$/.test(formData.directorId.replace(/\s/g, ''))) {
      newErrors.directorId = 'Director ID must be 15 digits';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const individualData = {
        ...formData,
        generation: parseInt(formData.generation),
        clientId: clientId,
        relationships: []
      };
      
      await onSave(individualData);
      setFormData(initialFormData);
      setErrors({});
      onClose();
    } catch (error) {
      console.error('Failed to save individual:', error);
      setErrors({ submit: 'Failed to save individual. Please try again.' });
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setFormData(initialFormData);
    setErrors({});
    onClose();
  };

  const toggleCorporateRole = (role) => {
    const currentRoles = formData.corporateRoles || [];
    if (currentRoles.includes(role)) {
      setFormData({ ...formData, corporateRoles: currentRoles.filter(r => r !== role) });
    } else {
      setFormData({ ...formData, corporateRoles: [...currentRoles, role] });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px] bg-white max-h-[90vh] overflow-y-auto" data-testid="add-individual-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading text-xl text-[#2C2420]">
            <Users className="w-5 h-5 text-[#4A3B32]" />
            {isEdit ? 'Edit Individual' : 'Add New Individual'}
          </DialogTitle>
          <DialogDescription className="text-[#5D524D]">
            {isEdit ? 'Update the details for this individual.' : 'Add a new individual to this family group.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name Section */}
          <div className="grid grid-cols-2 gap-4">
            {/* First Name */}
            <div className="space-y-2">
              <Label htmlFor="firstName" className="text-[#2C2420]">
                First Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="firstName"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                placeholder="e.g., John"
                className={`caffeine-input ${errors.firstName ? 'border-red-500' : ''}`}
                data-testid="individual-firstname-input"
              />
              {errors.firstName && <p className="text-sm text-red-500">{errors.firstName}</p>}
            </div>

            {/* Middle Name */}
            <div className="space-y-2">
              <Label htmlFor="middleName" className="text-[#2C2420]">Middle Name</Label>
              <Input
                id="middleName"
                value={formData.middleName}
                onChange={(e) => setFormData({ ...formData, middleName: e.target.value })}
                placeholder="e.g., William"
                className="caffeine-input"
                data-testid="individual-middlename-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Last Name */}
            <div className="space-y-2">
              <Label htmlFor="lastName" className="text-[#2C2420]">
                Last Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="lastName"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                placeholder="e.g., Smith"
                className={`caffeine-input ${errors.lastName ? 'border-red-500' : ''}`}
                data-testid="individual-lastname-input"
              />
              {errors.lastName && <p className="text-sm text-red-500">{errors.lastName}</p>}
            </div>

            {/* Preferred Name */}
            <div className="space-y-2">
              <Label htmlFor="preferredName" className="text-[#2C2420]">Preferred Name</Label>
              <Input
                id="preferredName"
                value={formData.preferredName}
                onChange={(e) => setFormData({ ...formData, preferredName: e.target.value })}
                placeholder="e.g., Johnny"
                className="caffeine-input"
                data-testid="individual-preferredname-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {/* Date of Birth */}
            <div className="space-y-2">
              <Label htmlFor="dateOfBirth" className="text-[#2C2420]">Date of Birth</Label>
              <Input
                id="dateOfBirth"
                type="date"
                value={formData.dateOfBirth}
                onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
                className="caffeine-input"
                data-testid="individual-dob-input"
              />
            </div>

            {/* Generation */}
            <div className="space-y-2">
              <Label className="text-[#2C2420]">Generation</Label>
              <Select
                value={formData.generation}
                onValueChange={(value) => setFormData({ ...formData, generation: value })}
              >
                <SelectTrigger className="caffeine-input" data-testid="individual-generation-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Gen 1 (Founders)</SelectItem>
                  <SelectItem value="2">Gen 2</SelectItem>
                  <SelectItem value="3">Gen 3</SelectItem>
                  <SelectItem value="4">Gen 4</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Family Role */}
          <div className="space-y-2">
            <Label className="text-[#2C2420]">
              Family Role <span className="text-red-500">*</span>
            </Label>
            <Select
              value={formData.familyRole}
              onValueChange={(value) => setFormData({ ...formData, familyRole: value })}
            >
              <SelectTrigger className={`caffeine-input ${errors.familyRole ? 'border-red-500' : ''}`} data-testid="individual-family-role-select">
                <SelectValue placeholder="Select family role..." />
              </SelectTrigger>
              <SelectContent>
                {FAMILY_ROLES.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    <div className="flex flex-col">
                      <span>{role.label}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.familyRole && <p className="text-sm text-red-500">{errors.familyRole}</p>}
          </div>

          {/* Corporate Roles (Multi-select chips) */}
          <div className="space-y-2">
            <Label className="text-[#2C2420] flex items-center gap-2">
              Corporate Roles
              <span className="text-xs text-[#8C8078] font-normal">(optional - can be assigned via Entities)</span>
            </Label>
            <div className="flex flex-wrap gap-2 p-3 rounded-lg border border-[#E6E0D9] bg-[#FAF8F5]">
              {CORPORATE_ROLES.map((role) => {
                const isSelected = (formData.corporateRoles || []).includes(role.value);
                return (
                  <button
                    key={role.value}
                    type="button"
                    onClick={() => toggleCorporateRole(role.value)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                      isSelected
                        ? 'bg-[#4A3B32] text-white'
                        : 'bg-white border border-[#D9D6D0] text-[#5D524D] hover:border-[#4A3B32] hover:text-[#4A3B32]'
                    }`}
                    data-testid={`corporate-role-${role.value.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {role.label}
                    {isSelected && <X className="w-3 h-3 ml-1.5 inline" />}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Director ID */}
          <div className="space-y-2">
            <Label htmlFor="directorId" className="text-[#2C2420] flex items-center gap-2">
              Director ID
              <span className="inline-flex items-center" title="Australian Director Identification Number (15 digits)">
                <Info className="w-3.5 h-3.5 text-[#8C8078]" />
              </span>
            </Label>
            <Input
              id="directorId"
              value={formData.directorId}
              onChange={(e) => setFormData({ ...formData, directorId: e.target.value })}
              placeholder="e.g., 036 123 456 789 012"
              className={`caffeine-input ${errors.directorId ? 'border-red-500' : ''}`}
              data-testid="individual-director-id-input"
            />
            {errors.directorId && <p className="text-sm text-red-500">{errors.directorId}</p>}
            <p className="text-xs text-[#8C8078]">15-digit Australian Director Identification Number</p>
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-[#2C2420]">Email</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="e.g., john@example.com"
              className="caffeine-input"
              data-testid="individual-email-input"
            />
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-[#2C2420]">Phone</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              placeholder="e.g., +61 412 345 678"
              className="caffeine-input"
              data-testid="individual-phone-input"
            />
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label htmlFor="address" className="text-[#2C2420]">Address</Label>
            <AddressAutocomplete
              value={formData.address}
              onChange={(addressData) => {
                if (addressData) {
                  setFormData({ ...formData, address: addressData.formatted });
                } else {
                  setFormData({ ...formData, address: '' });
                }
              }}
              onInputChange={(value) => setFormData({ ...formData, address: value })}
              placeholder="Start typing to search addresses..."
              countryCodes="au,nz,gb,us"
              data-testid="individual-address-input"
            />
            <p className="text-xs text-[#8C8078]">Powered by OpenStreetMap</p>
          </div>

          {/* Drivers Licence */}
          <div className="space-y-2">
            <Label htmlFor="driversLicence" className="text-[#2C2420]">Driver's Licence</Label>
            <Input
              id="driversLicence"
              value={formData.driversLicence}
              onChange={(e) => setFormData({ ...formData, driversLicence: e.target.value })}
              placeholder="e.g., DL12345678"
              className="caffeine-input"
              data-testid="individual-licence-input"
            />
          </div>

          {/* Confidential Toggle */}
          <div className="flex items-center justify-between py-2">
            <div>
              <Label className="text-[#2C2420]">Confidential</Label>
              <p className="text-xs text-[#8C8078]">Mark as confidential/restricted access</p>
            </div>
            <Switch
              checked={formData.confidential}
              onCheckedChange={(checked) => setFormData({ ...formData, confidential: checked })}
              data-testid="individual-confidential-toggle"
            />
          </div>

          {errors.submit && <p className="text-sm text-red-500 text-center">{errors.submit}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} className="caffeine-btn-ghost" data-testid="cancel-add-individual-btn">
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} className="caffeine-btn" data-testid="save-individual-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : (isEdit ? 'Save Changes' : 'Add Individual')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
